import { GenericTable } from "@shared/components/GenericTable";
import { TableColumn } from "@shared/types/table";
import { Badge } from "@shared/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@shared/ui/card";
import { useCallback, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { useComplianceHistory } from "@features/ax/assetRegistry/networks/hooks";
import { PageHeader } from "@shared/components/PageHeader";
import { PAGE_SIZE } from "@shared/utils/constants";
import { Button } from "@shared/ui/button";
import { Eye } from "lucide-react";
import { ComplianceReportDialog } from "../../../../../../../shared/complianceReport";

export const HistoryLanding = () => {
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [currentPage, setCurrentPage] = useState(1);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedReportData, setSelectedReportData] = useState();
  const facilityEntity = 0;

  const { data: historyData } = useComplianceHistory({
    entity_id: id,
    entity_type: facilityEntity,
  });

  const totalItems = historyData?.results?.length || 0;
  const totalPages = Math.ceil(totalItems / PAGE_SIZE);
  const startIndex = (currentPage - 1) * PAGE_SIZE;

  const pagination = useMemo(
    () => ({
      currentPage,
      totalPages,
      totalItems,
      itemsPerPage: PAGE_SIZE,
      startItem: startIndex + 1,
      endItem: Math.min(currentPage * PAGE_SIZE, totalItems),
    }),
    [currentPage, totalPages, totalItems, startIndex]
  );

  const handleViewReport: (row: any) => void = (row: any) => {
    setIsDialogOpen(true);
    setSelectedReportData(row);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setSelectedReportData(null);
  };

  // Compliance History Table Columns
  const complianceHistoryColumns: TableColumn<any>[] = [
    {
      key: "reportDate",
      header: "Report Date",
      type: "text",
      render: (value) => <span className="font-medium ml-4">{value}</span>,
    },
    {
      key: "analysisPeriod",
      header: "Analysis Period",
      type: "text",
      render: (value) => (
        <span className="text-sm text-gray-600 ml-4">{value}</span>
      ),
    },
    {
      key: "parametersMonitored",
      header: "Parameters Monitored",
      type: "custom",
      render: (value) => (
        <Badge variant="outline" className="bg-blue-50 text-blue-700 ml-6">
          {value}
        </Badge>
      ),
    },
    {
      key: "inCompliance",
      header: "In Compliance",
      type: "custom",
      render: (value) => (
        <Badge className="bg-green-100 text-green-800 ml-6">{value}</Badge>
      ),
    },
    {
      key: "violations",
      header: "Violations",
      type: "custom",
      render: (value) => (
        <Badge
          className={
            value > 0
              ? "bg-red-100 text-red-800"
              : "bg-green-100 text-green-800"
          }
        >
          {value}
        </Badge>
      ),
    },
    {
      key: "assessedBy",
      header: "Assessed By",
      type: "text",
      render: (value) => (
        <span className="text-gray-600 ml-6">{value || "N/A"}</span>
      ),
    },
    {
      key: "actions",
      header: "Actions",
      render: (value, row) => {
        return (
          <div className="flex items-center gap-2">
            <Button
              size="icon"
              variant="ghost"
              onClick={() => handleViewReport(row)}
              tooltip="View Report"
            >
              <Eye className="h-4 w-4" />
            </Button>
          </div>
        );
      },
    },
  ];

  const handlePageChange = useCallback(
    (newPage: number) => {
      const newParams = new URLSearchParams(searchParams);
      newParams.set("page", newPage.toString());

      setSearchParams(newParams, { replace: true });
      setCurrentPage(newPage);
    },
    [searchParams, setSearchParams]
  );

  return (
    <div className="space-y-6">
      {/* Compliance Calculation History */}
      <Card>
        <CardHeader>
          <CardTitle className="text-xl">
            <PageHeader
              title="Compliance Calculation History"
              subtitle="Historical record of calculated compliance reports"
            />
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0 mt-3">
          <GenericTable
            data={historyData?.results || []}
            columns={complianceHistoryColumns}
            rowKey="id"
            pagination={pagination}
            onPageChange={handlePageChange}
            emptyMessage="No compliance history found"
          />
        </CardContent>
      </Card>

      <ComplianceReportDialog
        isOpen={isDialogOpen}
        onClose={handleCloseDialog}
        reportData={selectedReportData}
      />
    </div>
  );
};
